import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '@/App';
import {
  isPushSupported,
  pushPermission,
  wasRecentlyDismissed,
  markDismissed,
  subscribeToPush,
  ensureServiceWorker,
} from '@/lib/push';

/**
 * Banner asking the user to opt into push notifications.
 * Trigger rules:
 *  - user is logged in
 *  - browser supports web push
 *  - permission is still 'default' (not 'granted' or 'denied')
 *  - user hasn't dismissed in the last 30 days
 *  - fires when `appinstalled` event fires (from the PWA install flow), otherwise ~10s after mount
 */
export function PushNotificationsPrompt() {
  const { user } = useContext(AuthContext);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  const shouldShow = useCallback(() => {
    if (!user) return false;
    if (!isPushSupported()) return false;
    if (pushPermission() !== 'default') return false;
    if (wasRecentlyDismissed()) return false;
    return true;
  }, [user]);

  useEffect(() => {
    if (!shouldShow()) return;

    // Register the SW quietly so `PushManager` is ready when the user opts in.
    ensureServiceWorker().catch(() => {});

    let timer = null;
    const onInstalled = () => {
      if (shouldShow()) setVisible(true);
    };
    window.addEventListener('appinstalled', onInstalled);

    // Fallback: show ~10s after the user lands, unless they navigate away first.
    timer = setTimeout(() => {
      if (shouldShow()) setVisible(true);
    }, 10000);

    return () => {
      window.removeEventListener('appinstalled', onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, [shouldShow]);

  const handleEnable = async () => {
    setBusy(true);
    const res = await subscribeToPush();
    setBusy(false);
    if (res.ok) {
      toast.success("You're subscribed to alerts");
      setVisible(false);
    } else if (res.reason === 'denied') {
      toast.error('Notifications blocked in your browser settings');
      markDismissed();
      setVisible(false);
    } else if (res.reason === 'unsupported') {
      toast.info('Push notifications are not supported in this browser');
      setVisible(false);
    } else {
      toast.error('Could not enable notifications. Try again later.');
    }
  };

  const handleDismiss = () => {
    markDismissed();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-prompt"
      className="fixed left-3 right-3 md:left-auto md:right-6 md:w-96 z-50 rounded-2xl shadow-xl border border-border bg-white overflow-hidden"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)' }}
      role="dialog"
      aria-label="Enable notifications"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
            <Bell className="w-5 h-5 text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Stay in the loop</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Get instant alerts for new messages, rent-due reminders, and matching listings.
            </p>
          </div>
          <button
            data-testid="push-prompt-dismiss"
            onClick={handleDismiss}
            className="p-1 -mt-1 -mr-1 rounded-md hover:bg-muted text-muted-foreground"
            aria-label="Dismiss notifications prompt"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            data-testid="push-prompt-enable"
            onClick={handleEnable}
            disabled={busy}
            size="sm"
            className="flex-1 rounded-full"
          >
            <Bell className="w-4 h-4 mr-1" /> {busy ? 'Enabling…' : 'Enable notifications'}
          </Button>
          <Button
            data-testid="push-prompt-later"
            onClick={handleDismiss}
            variant="outline"
            size="sm"
            className="rounded-full"
          >
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}
